import { useEffect, useRef, useState } from "react";

const DRAFT_KEY_PREFIX = "teco-draft:";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEBOUNCE_MS = 500;

function readDraft<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const { value, savedAt } = JSON.parse(raw);
    if (Date.now() - savedAt > DRAFT_TTL_MS) return null;
    return value as T;
  } catch {
    return null;
  }
}

// Continuously autosaves `currentValue` to localStorage under `draftKey` so
// an in-progress form survives an unplanned remount (e.g. a session-expiry
// redirect to /login and back). Call clearDraft() once the form is submitted.
export function useFormDraft<T>(draftKey: string, currentValue: T) {
  const storageKey = `${DRAFT_KEY_PREFIX}${draftKey}`;
  const [restoredDraft] = useState<T | null>(() => readDraft<T>(storageKey));
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);

    timeoutRef.current = window.setTimeout(() => {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ value: currentValue, savedAt: Date.now() })
      );
    }, DEBOUNCE_MS);

    return () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, JSON.stringify(currentValue)]);

  const clearDraft = () => localStorage.removeItem(storageKey);

  return { restoredDraft, clearDraft };
}
